// §34.6 — which extensions are media, and what MIME each blob URL is stamped with.
//
// **It lives in `assets/` because two built-ins need it** (D-170, D-508): Media claims these types
// and Peek previews them, and §6.5 forbids Explorer importing another app. `assets/**` is the one
// place two built-ins may share code, exactly as `assets/archive/format.ts` is for the zip reader
// and `assets/pdf/` is for the PDF one.
//
// Unlike those two it is imported **statically** by both, and that is deliberate rather than an
// oversight: this is one table of eight strings and two lookups, so a dynamic `import()` would buy
// nothing and would make Peek's audio branch asynchronous for no reason. The rule D-508 is actually
// about is *weight* — fflate and a PDF parser are heavy and belong behind a thunk. A frozen record
// is not.

/** §34.6 — the two families Media claims, and the only distinction the queue rules need. */
export type MediaKind = 'audio' | 'video';

/**
 * §34.6's audio types.
 *
 * The MIME is not decoration. Playback goes through a **blob URL** built from VFS bytes, and a blob
 * with no type makes the element sniff — which Chromium does well and Safari does not. Naming the
 * type from the extension we already matched on is free and removes the guess.
 *
 * `.m4a` is `audio/mp4`, not `audio/m4a`: the container is MP4 and that is what browsers register a
 * decoder for. Getting it wrong is the difference between an AAC file playing and §34.6's codec
 * refusal appearing for a file the browser can in fact play.
 */
const AUDIO_TYPES: Readonly<Record<string, string>> = {
  '.mp3': 'audio/mpeg',
  '.wav': 'audio/wav',
  '.ogg': 'audio/ogg',
  '.m4a': 'audio/mp4',
};

/** §34.6's video types. */
const VIDEO_TYPES: Readonly<Record<string, string>> = {
  '.mp4': 'video/mp4',
  '.webm': 'video/webm',
};

/**
 * `audio`, `video`, or `null` for a path Media does not claim.
 *
 * Takes the **extension**, already lowercased, rather than a path: the two callers arrive with
 * different types (`VfsPath` in Peek, `VfsPath | string` in the app) and `shared`'s `extname` is the
 * one function that should be deciding what an extension is.
 */
export function kindOfExtension(ext: string): MediaKind | null {
  if (ext in AUDIO_TYPES) return 'audio';
  if (ext in VIDEO_TYPES) return 'video';
  return null;
}

/** The blob type for a claimed extension. Empty string for one Media does not claim. */
export function mimeOfExtension(ext: string): string {
  return AUDIO_TYPES[ext] ?? VIDEO_TYPES[ext] ?? '';
}

/** Every extension Media registers — the fixture both registry and Peek tests enumerate against. */
export function mediaExtensions(): { audio: string[]; video: string[] } {
  return { audio: Object.keys(AUDIO_TYPES), video: Object.keys(VIDEO_TYPES) };
}

/**
 * `m:ss`, or `h:mm:ss` once there is an hour to show. `--:--` for a duration we do not have.
 *
 * Shared for the same reason the table is: Media's transport prints it and Peek's metadata line
 * prints it, and two implementations of "how do we write a duration" is how a desktop ends up
 * showing `1:05` in one window and `65s` in another.
 *
 * The `--:--` arm is not decoration. `HTMLMediaElement.duration` is `NaN` until metadata arrives
 * and it reaches this function on every render before then, so the alternative is `NaN:NaN`.
 */
export function formatTime(seconds: number): string {
  if (!Number.isFinite(seconds) || seconds < 0) return '--:--';
  const whole = Math.floor(seconds);
  const s = whole % 60;
  const m = Math.floor(whole / 60) % 60;
  const h = Math.floor(whole / 3600);
  const pad = (n: number): string => String(n).padStart(2, '0');
  return h > 0 ? `${h}:${pad(m)}:${pad(s)}` : `${m}:${pad(s)}`;
}
